<script lang="ts" setup>
import { useTemplateRef, computed } from 'vue';
import { useElementHover } from '@vueuse/core';
import { useFloating, flip, autoUpdate, shift, hide, type Middleware } from '@floating-ui/vue';
import { hasSlotContent } from '../../../utils';
import { useTableContext } from '../shared';

const { tableRef } = useTableContext();

const rootRef = useTemplateRef('root');
const referenceRef = useTemplateRef('reference');
const floatingRef = useTemplateRef('floating');

const isRowHovered = useElementHover(rootRef, {
  delayEnter: 25,
  delayLeave: 25,
});

const isActionsHovered = useElementHover(floatingRef, {
  delayEnter: 25,
  delayLeave: 25,
});

const isHovered = computed(() => isRowHovered.value || isActionsHovered.value);

const middleware = computed<Middleware[]>(() => [
  flip({
    boundary: tableRef.value ?? 'clippingAncestors',
  }),
  shift({
    boundary: tableRef.value ?? 'clippingAncestors',
  }),
  hide({ strategy: 'referenceHidden' }),
]);

const { floatingStyles, middlewareData } = useFloating(referenceRef, floatingRef, {
  placement: 'bottom-start',
  middleware,
  whileElementsMounted: autoUpdate,
  open: isHovered,
  transform: false,
});

// TODO: keep hover styles when actions are hovered
</script>

<template>
  <div ref="root" :class="$style.root">
    <slot />
    <template v-if="hasSlotContent($slots.actions)">
      <div :class="$style.actionsReferenceWrapper">
        <div ref="reference" :class="$style.actionsReference"></div>
      </div>
      <Teleport v-if="isHovered" to="body">
        <div
          ref="floating"
          :class="$style.actionsWrapper"
          :style="{
            ...floatingStyles,
            visibility: middlewareData.hide?.escaped || middlewareData.hide?.referenceHidden ? 'hidden' : undefined,
          }"
        >
          <div :class="$style.actions">
            <slot name="actions" />
          </div>
        </div>
      </Teleport>
    </template>
  </div>
</template>

<style lang="scss" module>
.root {
  position: relative;
}

.actionsReferenceWrapper {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  pointer-events: none;
}

.actionsReference {
  position: sticky;
  left: 44px;
  height: 100%;
  display: inline-block;
  width: 48px;
}

.actionsWrapper {
  position: absolute;
  padding: 4px 24px 4px 0;
}

.actions {
  display: inline-flex;
  border-radius: 10px;
  border: 1px solid #e4e5e8;
  background: #fff;
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);
}
</style>
